import { useState } from 'react';
import {
  PhoneIcon,
  ClockIcon,
  UserGroupIcon,
  CheckCircleIcon,
  PhoneArrowUpRightIcon,
} from '@heroicons/react/24/outline';
import { useQuery } from '@tanstack/react-query';

import { Card, Badge, Spinner } from '@realvisor/ui';
import { usePermissions } from '@/hooks/usePermissions';
import { useProject } from '@/contexts/ProjectContext';
import { api } from '@/lib/api';

interface Lead {
  id: string;
  status: string;
  notes: string | null;
  contact: {
    id: string;
    firstName: string | null;
    lastName: string | null;
    phone: string | null;
    email: string | null;
  };
  currentStage: {
    name: string;
    color: string | null;
  } | null;
  project: {
    name: string;
  } | null;
}

interface Activity {
  id: string;
  type: string;
  subject: string;
  meta: Record<string, any>;
  createdAt: string;
  contact: {
    id: string;
    firstName: string | null;
    lastName: string | null;
  } | null;
}

export default function CallCenterDashboard() {
  const { role } = usePermissions();
  const { currentProject } = useProject();

  // Fetch assigned leads (call queue)
  const { data: leadsData, isLoading: leadsLoading } = useQuery({
    queryKey: ['cc-leads', currentProject?.id],
    queryFn: () => {
      const params = new URLSearchParams({ status: 'open', limit: '20' });
      if (currentProject?.id) params.set('projectId', currentProject.id);
      return api.get<{ data: Lead[]; meta: any }>(`/leads?${params}`);
    },
  });

  // Fetch recent call activities
  const { data: activitiesData, isLoading: activitiesLoading } = useQuery({
    queryKey: ['cc-activities', currentProject?.id],
    queryFn: () =>
      api.get<{ data: Activity[]; total: number }>('/activities?type=call&limit=10'),
  });

  const leads = leadsData?.data ?? [];
  const activities = activitiesData?.data ?? [];

  // Calculate today's stats from activities
  const today = new Date().toISOString().split('T')[0] ?? '';
  const todayCalls = activities.filter(
    (a) => a.createdAt.startsWith(today),
  );
  const totalDuration = todayCalls.reduce(
    (sum, a) => sum + (a.meta?.['duration'] ?? 0),
    0,
  );

  const formatDuration = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const formatTime = (iso: string) => {
    return new Date(iso).toLocaleTimeString('cs-CZ', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getRoleBadge = () => {
    switch (role) {
      case 'cc_operator':
        return <Badge variant="danger">Operátor</Badge>;
      case 'cc_supervisor':
        return <Badge variant="warning">Supervizor</Badge>;
      case 'cc_manager':
        return <Badge variant="info">CC Manažer</Badge>;
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Call Centrum</h1>
          <p className="mt-1 text-sm text-gray-500">
            Fronta hovorů a přehled aktivity
          </p>
        </div>
        {getRoleBadge()}
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card padding="md">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-blue-50 p-2">
              <UserGroupIcon className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Ke kontaktování</p>
              <p className="text-2xl font-bold text-gray-900">{leads.length}</p>
            </div>
          </div>
        </Card>
        <Card padding="md">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-green-50 p-2">
              <PhoneIcon className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Hovory dnes</p>
              <p className="text-2xl font-bold text-gray-900">{todayCalls.length}</p>
            </div>
          </div>
        </Card>
        <Card padding="md">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-purple-50 p-2">
              <ClockIcon className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Celková doba</p>
              <p className="text-2xl font-bold text-gray-900">{formatDuration(totalDuration)}</p>
            </div>
          </div>
        </Card>
        <Card padding="md">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-orange-50 p-2">
              <CheckCircleIcon className="h-5 w-5 text-orange-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Prům. délka</p>
              <p className="text-2xl font-bold text-gray-900">
                {todayCalls.length > 0 ? formatDuration(Math.round(totalDuration / todayCalls.length)) : '0:00'}
              </p>
            </div>
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Call Queue — leads to contact */}
        <Card padding="none">
          <div className="border-b border-gray-200 px-4 py-3">
            <h2 className="text-lg font-semibold text-gray-900">
              Fronta hovorů
            </h2>
          </div>
          {leadsLoading ? (
            <div className="flex justify-center py-8">
              <Spinner />
            </div>
          ) : leads.length === 0 ? (
            <div className="py-8 text-center text-sm text-gray-500">
              Žádné leady ve frontě
            </div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {leads.map((lead) => (
                <li key={lead.id} className="flex items-center justify-between px-4 py-3 hover:bg-gray-50">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-gray-900">
                      {lead.contact.firstName} {lead.contact.lastName}
                    </p>
                    <p className="text-sm text-gray-500">
                      {lead.contact.phone || lead.contact.email || 'Bez kontaktu'}
                    </p>
                    {lead.currentStage && (
                      <span
                        className="mt-1 inline-block rounded-full px-2 py-0.5 text-xs font-medium"
                        style={{
                          backgroundColor: (lead.currentStage.color || '#E5E7EB') + '20',
                          color: lead.currentStage.color || '#6B7280',
                        }}
                      >
                        {lead.currentStage.name}
                      </span>
                    )}
                  </div>
                  {lead.contact.phone && (
                    <button
                      className="ml-3 flex items-center gap-1 rounded-lg bg-green-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-700 transition-colors"
                      title={`Zavolat ${lead.contact.phone}`}
                    >
                      <PhoneArrowUpRightIcon className="h-4 w-4" />
                      Volat
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Card>

        {/* Recent calls */}
        <Card padding="none">
          <div className="border-b border-gray-200 px-4 py-3">
            <h2 className="text-lg font-semibold text-gray-900">
              Poslední hovory
            </h2>
          </div>
          {activitiesLoading ? (
            <div className="flex justify-center py-8">
              <Spinner />
            </div>
          ) : activities.length === 0 ? (
            <div className="py-8 text-center text-sm text-gray-500">
              Zatím žádné hovory
            </div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {activities.map((activity) => {
                const isInbound = activity.meta?.['callDirection'] === 'inbound';
                const duration = activity.meta?.['duration'];
                const hasRecording = !!activity.meta?.['recordingUrl'];

                return (
                  <li key={activity.id} className="flex items-center gap-3 px-4 py-3">
                    <div
                      className={`rounded-full p-1.5 ${
                        isInbound ? 'bg-blue-50 text-blue-600' : 'bg-green-50 text-green-600'
                      }`}
                    >
                      <PhoneIcon className="h-4 w-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-gray-900">
                        {activity.contact
                          ? `${activity.contact.firstName || ''} ${activity.contact.lastName || ''}`.trim()
                          : activity.subject}
                      </p>
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <span>{formatTime(activity.createdAt)}</span>
                        {duration != null && (
                          <>
                            <span>·</span>
                            <span>{formatDuration(duration)}</span>
                          </>
                        )}
                        {hasRecording && (
                          <>
                            <span>·</span>
                            <span className="text-red-500">REC</span>
                          </>
                        )}
                      </div>
                    </div>
                    <span
                      className={`text-xs font-medium ${
                        isInbound ? 'text-blue-600' : 'text-green-600'
                      }`}
                    >
                      {isInbound ? 'Příchozí' : 'Odchozí'}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
}
